import React, { useState } from 'react';
import { Alert } from '../../../shared/components/Alert';
import { TextInput } from '../../../shared/components/TextInput';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { CommercialBaseSelect } from '../../base-comercial/components/CommercialBaseSelect';
import { useProporOportunidade } from '../hooks';
import { ActionModal } from './ActionModal';
import type { Oportunidade } from '../api';

interface Props {
  opp: Oportunidade;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Modal de geração de proposta (task 12.2).
 *
 * Integra o `CommercialBaseSelect` (task 12.1): o usuário escolhe uma
 * Base Comercial **vigente** do cliente da oportunidade (RN-COM-018) e
 * as condições são carregadas automaticamente. Sem Base selecionada,
 * gera proposta do zero e sinaliza que nenhuma Base foi aplicada
 * (RN-COM-019).
 */
export const ProposalModal: React.FC<Props> = ({ opp, isOpen, onClose, onSuccess }) => {
  const [commercialBaseId, setCommercialBaseId] = useState('');
  const [descontoPercentual, setDescontoPercentual] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useProporOportunidade(opp.id);

  const submit = async () => {
    setErr(null);
    try {
      await mut.mutateAsync({
        commercialBaseId: commercialBaseId || undefined,
        descontoPercentual: descontoPercentual ? Number(descontoPercentual) : undefined,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao gerar proposta.'));
    }
  };

  const baseAplicada = !!commercialBaseId;

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Gerar proposta"
      description="Selecione a Base Comercial vigente do cliente para carregar as condições negociadas (RN-COM-018). Sem Base, a proposta é gerada do zero (RN-COM-019)."
      submitLabel="Gerar proposta"
      submitVariant="primary"
      isSubmitting={mut.isPending}
      submitError={err}
    >
      <div className="space-y-3">
        <CommercialBaseSelect
          clientId={opp.clientId}
          value={commercialBaseId}
          onChange={setCommercialBaseId}
          label="Base Comercial vigente"
        />
        {baseAplicada ? (
          <Alert variant="info">
            Condições da Base selecionada serão carregadas automaticamente na proposta.
          </Alert>
        ) : (
          <Alert variant="warning">
            Nenhuma Base Comercial aplicada. A proposta será gerada sem condições
            pré-carregadas.
          </Alert>
        )}
        <TextInput
          label="Desconto (%) — opcional, sujeito à alçada"
          type="number"
          min={0}
          max={100}
          value={descontoPercentual}
          onChange={(e) => setDescontoPercentual(e.target.value)}
        />
      </div>
    </ActionModal>
  );
};
